'use client';
import React from 'react';
import styled, { css } from '../references';
import { ScrollContainer } from './scrollContainer';
import ReactDOM from 'react-dom';

interface Props {
  children?: React.ReactNode;
  className?: string;
  maxWidth?: number;
  minWidth?: number | `100%`;
  parentRectClassName?: string;
  positionLeft?: number | null;
  positionRight?: number | null;
  positionTop?: number | null;
}

const findParentWith = (constrainingDiv: HTMLElement, predicate: (el: HTMLElement) => boolean) => {
  let parentEl = constrainingDiv.parentElement;
  let parentRect = {
    height: 0,
    left: 0,
    top: 0,
    width: 0,
  };

  let scrollVisible = false;
  while (parentEl) {
    const clientHeight = parentEl?.clientHeight ?? 0;
    const scrollHeight = parentEl?.scrollHeight ?? 0;
    if (clientHeight < scrollHeight) {
      scrollVisible = true;
    }
    if (predicate(parentEl)) {
      // Bounding rect has numbers for width/height, whereas style can have 'auto'
      parentRect = parentEl.getBoundingClientRect();
      break;
    }
    parentEl = parentEl.parentElement;
  }

  return { parentEl, parentRect, scrollVisible };
};

export const ConstrainingDiv = (
  constrainingDiv: HTMLDivElement,
  maxWidth?: number,
  minWidth?: number | `100%`,
  parentRectClassName?: string,
  positionRight?: number | null,
) => {
  const refRect = constrainingDiv.getBoundingClientRect();

  const { parentEl, parentRect, scrollVisible } = findParentWith(constrainingDiv, parentEl => {
    const style = getComputedStyle(parentEl);
    return parentRectClassName ? parentEl.classList.contains(parentRectClassName) : style.overflow === 'hidden';
  });

  const remainingWidth = positionRight
    ? refRect.right - parentRect.left
    : parentRect.left + parentRect.width - refRect.left;

  const remainingHeight = parentRect.height - (refRect.top - parentRect.top);

  const finalMaxWidth = positionRight ? remainingWidth : remainingWidth - (scrollVisible ? 12 : 0);
  return {
    bottom: parentRect.top + parentRect.height,
    left: parentRect.left,
    maxHeight: remainingHeight,
    maxWidth: Math.min(maxWidth ?? Infinity, finalMaxWidth < 0 ? Infinity : finalMaxWidth),
    minWidth:
      minWidth === `100%`
        ? parentRect.width
        : Math.min(minWidth ?? Infinity, finalMaxWidth < 0 ? Infinity : finalMaxWidth),
    parent: parentEl,
    right: parentRect.left + parentRect.width,
    top: parentRect.top,
  };
};

/**
 * Creates a floating div whose size is restricted so that it will fit in a parent
 * with overflow: hidden ensuring that no part of its children will be hidden
 * due to extending outside of said parent.
 */
export const ConstrainedFloater: React.FC<Props> = props => {
  const [constrainedStyle, setConstrainedStyle] = React.useState<{
    bottom?: number;
    left?: number;
    maxHeight?: number;
    minHeight?: number;
    maxWidth?: number;
    minWidth?: number;
    parent?: HTMLElement | null;
    right?: number;
    top?: number;
    viewPort?: {
      bottom: number;
      left: number;
      right: number;
      top: number;
    };
  }>({
    maxWidth: props.maxWidth,
  });

  // for resize and zoom of browser
  const [resizeTrigger, setResizeTrigger] = React.useState(0);
  const resizeListener = React.useCallback(() => {
    setResizeTrigger(resizeDependency => resizeDependency + 1);
  }, []);
  React.useEffect(() => {
    window.addEventListener('resize', resizeListener);
    return () => {
      window.removeEventListener('resize', resizeListener);
    };
  }, [resizeListener]);

  const onConstrainingDiv = React.useCallback(
    (constrainingDiv: HTMLDivElement | null) => {
      if (constrainingDiv) {
        const { parentRect } = findParentWith(constrainingDiv, parentEl => {
          const style = getComputedStyle(parentEl);
          return style.backdropFilter ? style.backdropFilter !== 'none' : false;
        });
        const containerDiv = ConstrainingDiv(
          constrainingDiv,
          props.maxWidth,
          props.minWidth,
          props.parentRectClassName,
          props.positionRight,
        );

        setConstrainedStyle({
          ...containerDiv,
          bottom: containerDiv.bottom,
          left: containerDiv.left,
          right: containerDiv.right,
          top: containerDiv.top,
          viewPort: {
            bottom: parentRect.top + parentRect.height,
            left: parentRect.left,
            right: parentRect.left + parentRect.width,
            top: parentRect.top,
          },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.maxWidth, props.minWidth, props.parentRectClassName, props.positionRight, resizeTrigger],
  );

  const Content = (
    <ConstrainedFloaterStyle
      left={
        props.positionLeft !== undefined
          ? props.positionLeft === null
            ? `${constrainedStyle.left ? constrainedStyle.left - (constrainedStyle.viewPort?.left ?? 0) : 0}px`
            : `${props.positionLeft ? props.positionLeft - (constrainedStyle.viewPort?.left ?? 0) : 0}px`
          : 'auto'
      }
      maxHeight={constrainedStyle.maxHeight && `${constrainedStyle.maxHeight}px`}
      maxWidth={constrainedStyle.maxWidth && `${constrainedStyle.maxWidth}px`}
      minWidth={constrainedStyle.minWidth && `${constrainedStyle.minWidth}px`}
      ref={onConstrainingDiv}
      right={
        props.positionRight !== undefined
          ? props.positionRight === null
            ? `${constrainedStyle.right ? constrainedStyle.right - (constrainedStyle.viewPort?.right ?? 0) : 0}px`
            : `${props.positionRight ? props.positionRight - (constrainedStyle.viewPort?.right ?? 0) : 0}px`
          : 'auto'
      }
      top={
        props.positionTop !== undefined
          ? props.positionTop === null
            ? `${constrainedStyle.top ? constrainedStyle.top - (constrainedStyle.viewPort?.top ?? 0) : 0}px`
            : `${props.positionTop ? props.positionTop - (constrainedStyle.viewPort?.top ?? 0) : 0}px`
          : 'auto'
      }
    >
      <ScrollContainer className={props.className}>{props.children}</ScrollContainer>
    </ConstrainedFloaterStyle>
  );

  if (props.parentRectClassName && constrainedStyle.parent) {
    return ReactDOM.createPortal(Content, constrainedStyle.parent);
  } else {
    return Content;
  }
};

interface ConstrainedFloaterStyleProps {
  left: React.CSSProperties['left'];
  maxHeight: React.CSSProperties['maxHeight'];
  maxWidth: React.CSSProperties['maxWidth'];
  minWidth: React.CSSProperties['minWidth'];
  right: React.CSSProperties['right'];
  top: React.CSSProperties['top'];
}

const ConstrainedFloaterStyle = styled.div<ConstrainedFloaterStyleProps>`
  background-color: ${props => props.theme.colors.background};
  display: flex;
  ${props =>
    props.maxHeight &&
    css`
      max-height: ${props.maxHeight};
    `};
  ${props =>
    props.maxWidth &&
    css`
      max-width: ${props.maxWidth};
    `};
  ${props =>
    props.minWidth &&
    css`
      min-width: ${props.minWidth};
    `};
  overflow: hidden;
  position: fixed;
  left: ${props => props.left ?? 'auto'};
  right: ${props => props.right ?? 'auto'};
  top: ${props => props.top ?? '100%'};
  z-index: 100;
`;
